import {
  type Envelope,
  type Message,
  type Role,
  PROTOCOL_VERSION,
  ProtocolError,
  decodeEnvelope,
  encodeEnvelope,
} from "./protocol.js";
import type { Transport } from "./transport.js";

/// `Peer` is the protocol-aware wrapper around a `Transport`. Games
/// talk to `Peer` (not directly to the transport) so they get:
///
///   - typed `send()` / `onMessage()` for game payloads,
///   - automatic hello / ready handshake,
///   - periodic heartbeats + miss detection,
///   - graceful `bye` on shutdown.
///
/// Construction is two-step so the caller can attach listeners
/// before any inbound frame is delivered:
///
///   const peer = new Peer({ transport, role: "host", gameType: "hangman" });
///   peer.onMessage((m) => …);
///   peer.onStateChange((s) => …);
///   await peer.start();

export type PeerState =
  | "opening"
  | "handshaking"
  | "ready"
  | "closed";

interface PeerOptions {
  transport: Transport;
  role: Role;
  gameType: string;
  /// Heartbeat interval in ms. Default 2000 (every 2s). The peer
  /// transitions to `closed` if no inbound frame is seen for
  /// `3 * heartbeatMs`.
  heartbeatMs?: number;
  /// Clock source — exposed for tests that need deterministic
  /// timestamps. Production passes `Date.now`.
  now?: () => number;
}

type MessageHandler = (msg: Message) => void;
type StateHandler = (state: PeerState) => void;

export class Peer {
  private readonly transport: Transport;
  private readonly role: Role;
  private readonly gameType: string;
  private readonly heartbeatMs: number;
  private readonly now: () => number;

  private state: PeerState = "opening";
  private outSeq = 0;
  private lastInbound: number;
  private heartbeatHandle: ReturnType<typeof setInterval> | null = null;
  private remoteReady = false;
  private localReady = false;

  private readonly messageHandlers: Set<MessageHandler> = new Set();
  private readonly stateHandlers: Set<StateHandler> = new Set();

  constructor(opts: PeerOptions) {
    this.transport = opts.transport;
    this.role = opts.role;
    this.gameType = opts.gameType;
    this.heartbeatMs = opts.heartbeatMs ?? 2000;
    this.now = opts.now ?? Date.now;
    this.lastInbound = this.now();
  }

  onMessage(handler: MessageHandler): () => void {
    this.messageHandlers.add(handler);
    return () => this.messageHandlers.delete(handler);
  }

  onStateChange(handler: StateHandler): () => void {
    this.stateHandlers.add(handler);
    return () => this.stateHandlers.delete(handler);
  }

  currentState(): PeerState {
    return this.state;
  }

  async start(): Promise<void> {
    await this.transport.open(this.role, {
      onOpen: () => this.onTransportOpen(),
      onFrame: (bytes) => this.onFrame(bytes),
      onClose: (reason) => this.onTransportClose(reason),
      onError: (err) => this.fail(err),
    });
  }

  private onTransportClose(_reason?: string): void {
    if (this.state === "closed") return;
    this.transitionTo("closed");
    this.stopHeartbeat();
  }

  /// Send a game-level payload. The peer wraps it in a `game` message
  /// and an envelope and hands it to the transport.
  async sendGame(payload: unknown): Promise<void> {
    await this.send({ type: "game", payload });
  }

  /// Close the link with an optional reason. Sends a `bye` frame
  /// best-effort, then tears down the transport.
  async close(reason?: string): Promise<void> {
    if (this.state === "closed") return;
    try {
      await this.send({ type: "bye", reason: reason ?? "" });
    } catch {
      /* peer may already be gone; close anyway */
    }
    this.transitionTo("closed");
    this.stopHeartbeat();
    await this.transport.close();
  }

  private onTransportOpen(): void {
    this.transitionTo("handshaking");
    this.lastInbound = this.now();
    // Send hello immediately. The other peer's hello drives our
    // transition to `ready`.
    void this.send({
      type: "hello",
      version: PROTOCOL_VERSION,
      gameType: this.gameType,
    });
    this.startHeartbeat();
  }

  private onFrame(bytes: Uint8Array): void {
    let env: Envelope;
    try {
      env = decodeEnvelope(bytes);
    } catch (e) {
      this.fail(e as Error);
      return;
    }
    this.lastInbound = this.now();
    this.handle(env.msg);
  }

  private handle(msg: Message): void {
    switch (msg.type) {
      case "hello":
        // Mismatched game type aborts the session — there's no way
        // for hangman to talk to battleships.
        if (msg.gameType !== this.gameType) {
          this.fail(new ProtocolError(
            `game mismatch: peer wants ${msg.gameType}, we want ${this.gameType}`,
          ));
          return;
        }
        // Acknowledge by sending ready. Once both sides have sent
        // ready we move to `ready`.
        this.localReady = true;
        void this.send({ type: "ready" });
        this.maybeReady();
        return;
      case "ready":
        this.remoteReady = true;
        this.maybeReady();
        return;
      case "heartbeat":
        return; // already updated lastInbound
      case "bye":
        this.transitionTo("closed");
        this.stopHeartbeat();
        void this.transport.close();
        return;
      case "game":
        for (const h of this.messageHandlers) h(msg);
        return;
    }
  }

  private maybeReady(): void {
    if (this.localReady && this.remoteReady && this.state !== "ready") {
      this.transitionTo("ready");
    }
  }

  private async send(msg: Message): Promise<void> {
    if (this.state === "closed") return;
    const env: Envelope = { seq: this.outSeq++, at: this.now(), msg };
    const bytes = encodeEnvelope(env);
    try {
      await this.transport.send(bytes);
    } catch (e) {
      this.fail(e as Error);
    }
  }

  private startHeartbeat(): void {
    if (this.heartbeatHandle) return;
    this.heartbeatHandle = setInterval(() => {
      void this.send({ type: "heartbeat", at: this.now() });
      const missed = this.now() - this.lastInbound;
      if (missed > this.heartbeatMs * 3) {
        this.fail(new Error(`heartbeat lost (${missed} ms)`));
      }
    }, this.heartbeatMs);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatHandle) {
      clearInterval(this.heartbeatHandle);
      this.heartbeatHandle = null;
    }
  }

  private transitionTo(next: PeerState): void {
    if (this.state === next) return;
    this.state = next;
    for (const h of this.stateHandlers) h(next);
  }

  private fail(err: Error): void {
    if (this.state === "closed") return;
    this.transitionTo("closed");
    this.stopHeartbeat();
    void this.transport.close();
    // surface as an error message via state-change handlers only —
    // the caller listens via `onStateChange("closed")` and can read
    // the last error from their own bookkeeping if they care.
    void err;
  }
}
